/**
 * content.js — static pages, lab pages, news and search results.
 *
 * Galleries on both static pages and lab pages now go through the shared
 * slider + lightbox pair, so the lab gallery gets the navigation buttons it
 * was missing and the arrows point the correct way under /fa (they are
 * logical-property positioned, and the chevrons carry .nv-icon-flip).
 */

import {esc, img, icon, truncate, emptyState} from '../lib/dom.js';
import {gettext, getLang, num, formatDate, localePath} from '../lib/i18n.js';
import {sliderMarkup} from '../ui/slider.js';
import {galleryMarkup} from '../ui/lightbox.js';

/* ------------------------------------------------------------------ *
 * Shared
 * ------------------------------------------------------------------ */

function pageHero({title, subtitle, breadcrumbs = [], image = '', compact = false, particles = null}) {
    // ui/particles.js reads this attribute; leaving it off means no canvas is
    // created at all, which is the right cost for a page with no effect set.
    const particleAttr = particles
        ? ` data-hero-particles='${esc(JSON.stringify(particles))}'`
        : '';

    return `
    <section class="nv-page-hero ${compact ? 'nv-page-hero--compact' : ''}"${particleAttr}>
      ${image ? `<img src="${esc(image)}" alt="" class="nv-page-hero__bg"
                      onerror="this.style.display='none'">` : ''}
      <div class="nv-container nv-page-hero__inner">
        ${breadcrumbs.length ? `
          <nav class="nv-breadcrumb" aria-label="${gettext('Breadcrumb')}">
            <a href="${esc(localePath('/'))}">${gettext('Home')}</a>
            ${breadcrumbs.map((crumb) => `
              <i class="fa-solid fa-chevron-left nv-breadcrumb__sep nv-icon-flip" aria-hidden="true"></i>
              ${crumb.link
        ? `<a href="${esc(localePath(crumb.link))}">${esc(crumb.label)}</a>`
        : `<span>${esc(crumb.label)}</span>`}`).join('')}
          </nav>` : ''}
        <h1 class="nv-page-hero__title">${esc(title)}</h1>
        ${subtitle ? `<p class="nv-page-hero__subtitle">${esc(subtitle)}</p>` : ''}
      </div>
    </section>`;
}

/** A gallery presented as a slider with fixed buttons, plus lightbox tiles. */
function gallerySection(images, {heading, group}) {
    if (!images?.length) return '';

    // Few enough to fit — show the grid; otherwise a slider earns its keep.
    const asSlider = images.length > 4;

    const body = asSlider
        ? sliderMarkup(images.map((image) => `
        <a href="${esc(image.image || '#')}" class="nv-gallery__item"
           data-lightbox="${esc(group)}"
           data-lightbox-src="${esc(image.image || '')}"
           data-lightbox-caption="${esc(image.caption || '')}">
          ${img(image.image, image.caption || '')}
          <span class="nv-gallery__zoom">
            <i class="fa-solid fa-magnifying-glass-plus" aria-hidden="true"></i>
          </span>
          ${image.caption ? `
            <span class="nv-gallery__overlay">
              <span class="nv-gallery__caption">${esc(image.caption)}</span>
            </span>` : ''}
        </a>`), {variant: 'nv-slider--narrow', label: heading})
        : galleryMarkup(images, group);

    return `
    <section class="nv-section nv-lab-section">
      <div class="nv-section-head">
        <div class="nv-section-head__text">
          <h2 class="nv-section-title">${esc(heading)}</h2>
        </div>
      </div>
      ${body}
    </section>`;
}

/* ------------------------------------------------------------------ *
 * Static page
 * ------------------------------------------------------------------ */

export function renderStaticPage(data) {
    const page = data.page || data;

    const sections = (page.sections || []).map((section) => `
    <section class="nv-section nv-section--tight">
      <h2 class="nv-section-title">${esc(section.heading)}</h2>
      <div class="nv-prose">
        ${(section.paragraphs || []).map((p) => `<p>${esc(p.text)}</p>`).join('')}
      </div>
    </section>`).join('');

    return `
    ${pageHero({
        title: page.title,
        subtitle: page.subtitle,
        image: page.hero_image,
        breadcrumbs: [{label: page.title}],
    })}
    <div class="nv-container">
        ${page.content ? `<div class="nv-section nv-prose">${page.content}</div>` : ''}
        ${sections}
            ${page.app_extracted_path ? `
            <div class="nv-section">
                <iframe src="/media/${esc(page.app_extracted_path)}" style="width: 100%; height: 75vh; border: none; border-radius: 8px;" title="${esc(page.title)}"></iframe>
            </div>` : ''}
            ${gallerySection(page.gallery, {
        heading: gettext('Gallery'),
        group: 'page-gallery',
    })}
    </div>`;
}

/* ------------------------------------------------------------------ *
 * Lab page
 * ------------------------------------------------------------------ */

export function renderLab(data) {
    const lab = data.lab || data;

        let equipmentCategory = null;
    if (lab.equipments && lab.equipments.length) {
        const eqHtml = lab.equipments.map((item) => `
            <article class="nv-card" style="margin-bottom: var(--nv-space-3);">
              <div class="nv-card__media">
                ${item.image ? img(item.image, item.title) : `<span class="nv-card__placeholder">${icon('microscope')}</span>`}
              </div>
              <div class="nv-card__body">
                <h3 class="nv-card__title">${esc(item.title)}</h3>
                ${item.description ? `<p class="nv-card__text">${esc(truncate(item.description, 20))}</p>` : ''}
              </div>
            </article>`).join('');

        equipmentCategory = {
            is_equipment_block: true,
            name: gettext('Equipment'),
            column: 'right',
            html: `
            <div class="nv-stack--lg" style="margin-bottom: var(--nv-space-10);">
              <div class="nv-section-head" style="margin-bottom: var(--nv-space-4);"><div class="nv-section-head__text"><h2 class="nv-section-title" style="font-size: var(--nv-text-2xl); padding-bottom: var(--nv-space-2);">${gettext('Equipment')}</h2></div></div>
              <div class="nv-grid nv-grid--3">${eqHtml}</div>
            </div>`
        };
    }

    let categories = (lab.categories || []).sort((a, b) => (a.order || 0) - (b.order || 0));
    if (equipmentCategory) {
        let eqIndex = parseInt(lab.equipment_index);
        if (isNaN(eqIndex) || eqIndex < 0) eqIndex = 0;
        if (eqIndex >= categories.length) categories.push(equipmentCategory);
        else categories.splice(eqIndex, 0, equipmentCategory);
    }

    const renderCategory = (cat) => {
        if (cat.is_equipment_block) return cat.html;

        const isPeople = cat.type === 'person' ||
                         cat.name.includes('اعضا') ||
                         cat.name.includes('پژوهشگر') ||
                         cat.name.toLowerCase().includes(gettext('members').toLowerCase()) ||
                         cat.name.toLowerCase().includes(gettext('researcher').toLowerCase());
        const isEquipment = cat.type === 'equipment';

        if (isPeople) {
            const peopleHtml = (cat.items || []).map((item) => `
        <div class="nv-person">
          ${item.image
                ? `<img src="${esc(item.image)}" alt="${esc(item.text)}" class="nv-person__avatar">`
                : `<span class="nv-person__avatar" style="display:grid;place-items:center;">${icon('user')}</span>`}
          <span class="nv-person__name">${esc(item.text)}</span>
          ${item.description ? `<span class="nv-person__role">${esc(item.description)}</span>` : ''}
        </div>`).join('');
            return `
        <div class="nv-stack--lg" style="margin-bottom: var(--nv-space-10);">
          <div class="nv-section-head" style="margin-bottom: var(--nv-space-4);"><div class="nv-section-head__text"><h2 class="nv-section-title" style="font-size: var(--nv-text-2xl); padding-bottom: var(--nv-space-2);">${esc(cat.name)}</h2></div></div>
          <div class="nv-people-grid">${peopleHtml}</div>
        </div>`;
        } else if (isEquipment) {
            const eqHtml = (cat.items || []).map((item) => `
            <article class="nv-card" style="margin-bottom: var(--nv-space-3);">
              <div class="nv-card__media">
                ${item.image ? img(item.image, item.text) : `<span class="nv-card__placeholder">${icon('microscope')}</span>`}
              </div>
              <div class="nv-card__body">
                <h3 class="nv-card__title">${esc(item.text)}</h3>
                ${item.description ? `<p class="nv-card__text">${esc(truncate(item.description, 20))}</p>` : ''}
              </div>
            </article>`).join('');
            return `
            <div class="nv-stack--lg" style="margin-bottom: var(--nv-space-10);">
              <div class="nv-section-head" style="margin-bottom: var(--nv-space-4);"><div class="nv-section-head__text"><h2 class="nv-section-title" style="font-size: var(--nv-text-2xl); padding-bottom: var(--nv-space-2);">${esc(cat.name)}</h2></div></div>
              <div class="nv-grid nv-grid--3">${eqHtml}</div>
            </div>`;
        } else {
            const blocksHtml = (cat.items || []).map((item) => `
        <div class="nv-card" style="padding: var(--nv-space-4); margin-bottom: var(--nv-space-3);">
          <h3 class="nv-card__title" style="font-size: var(--nv-text-md); margin-bottom: var(--nv-space-2);">${esc(item.text)}</h3>
          ${item.description ? `<p class="nv-card__text">${esc(item.description)}</p>` : ''}
          ${(item.links || []).filter(Boolean).map(l => `<a href="${esc(l)}" target="_blank" class="nv-link-action" style="margin-top: var(--nv-space-2);"><span>${gettext('View')}</span> ${icon('arrow-right', {className: 'nv-icon-flip'})}</a>`).join(' ')}
        </div>`).join('');
            return `
        <div class="nv-stack--lg" style="margin-bottom: var(--nv-space-10);">
          <div class="nv-section-head" style="margin-bottom: var(--nv-space-4);"><div class="nv-section-head__text"><h2 class="nv-section-title" style="font-size: var(--nv-text-2xl); padding-bottom: var(--nv-space-2);">${esc(cat.name)}</h2></div></div>
          <div class="nv-stack">${blocksHtml}</div>
        </div>`;
        }
    };

    const rightCols = categories.filter(c => c.column === 'right' || c.column === undefined).map(renderCategory).join('');
    const leftCols = categories.filter(c => c.column === 'left').map(renderCategory).join('');

    const dynamicSections = (rightCols || leftCols) ? `
    <section class="nv-section">
      ${(rightCols && leftCols) ? `
        <div class="nv-split">
          <div>${rightCols}</div>
          <div class="nv-split__aside">${leftCols}</div>
        </div>
      ` : `
        <div>${rightCols}${leftCols}</div>
      `}
    </section>
  ` : '';

    return `
    ${pageHero({
        title: lab.title,
        subtitle: getLang() === 'fa' ? lab.title : '',
        image: lab.hero_background || lab.image,
        particles: lab.particles,
        breadcrumbs: [{label: gettext('Labs')}, {label: lab.title}],
    })}
    <div class="nv-container">
      ${lab.description ? `
        <section class="nv-section">
          <div class="nv-prose">${lab.description}</div>
        </section>` : ''}
 
      ${dynamicSections}

      ${lab.app_extracted_path ? `
        <section class="nv-section">
          <iframe src="/media/${esc(lab.app_extracted_path)}" style="width: 100%; height: 75vh; border: none; border-radius: 8px;" title="${esc(lab.title)}"></iframe>
        </section>` : ''}
        
      ${gallerySection(lab.gallery, {
        heading: gettext('Lab gallery'),
        group: 'lab-gallery',
    })}
    </div>`;
}

/* ------------------------------------------------------------------ *
 * News
 * ------------------------------------------------------------------ */

export function renderNewsList(data) {
    const items = data.news || data.items || [];

    const cards = items.map((item) => `
    <a href="${esc(localePath(`/news/${item.slug}`))}" class="nv-card">
      <div class="nv-card__media">
        ${item.cover_image ? img(item.cover_image, item.title)
        : `<span class="nv-card__placeholder">${icon('newspaper')}</span>`}
      </div>
      <div class="nv-card__body">
        <div class="nv-card__meta">
          ${icon('calendar-day', {solid: false})}
          <span class="nv-num">${esc(formatDate(item.published_at))}</span>
        </div>
        <h3 class="nv-card__title">${esc(item.title)}</h3>
        <p class="nv-card__text">${esc(truncate(item.description || item.content, 22))}</p>
      </div>
    </a>`).join('');

    return `
    ${pageHero({
        title: gettext('News & Events'),
        breadcrumbs: [{label: gettext('News')}],
        compact: true,
    })}
    <section class="nv-section">
      <div class="nv-container">
        ${cards
        ? `<div class="nv-grid nv-grid--3">${cards}</div>`
        : emptyState({
            iconName: 'newspaper',
            title: gettext('No news yet'),
        })}
      </div>
    </section>`;
}

export function renderNewsDetail(data) {
    const item = data.news || data;

    return `
    ${pageHero({
        title: item.title,
        breadcrumbs: [{label: gettext('News'), link: '/news/'}, {label: item.title}],
        compact: true,
    })}
    <section class="nv-section">
      <div class="nv-container nv-container--narrow">
        <div class="nv-article">
          <div class="nv-article__meta">
            <span class="nv-article__meta-item">
              ${icon('calendar-day', {solid: false})}
              <span class="nv-num">${esc(formatDate(item.published_at))}</span>
            </span>
          </div>
          ${item.cover_image
        ? `<img src="${esc(item.cover_image)}" alt="${esc(item.title)}" class="nv-article__cover">`
        : ''}
          <div class="nv-prose">${item.content || ''}</div>
        </div>

        <div class="nv-cluster" style="margin-block-start: var(--nv-space-8);">
          <a href="${esc(localePath('/news/'))}" class="nv-btn nv-btn--outline">
            <i class="fa-solid fa-arrow-left nv-icon-flip" aria-hidden="true"></i>
            <span>${gettext('Back to news')}</span>
          </a>
        </div>
      </div>
    </section>`;
}

/* ------------------------------------------------------------------ *
 * Search
 * ------------------------------------------------------------------ */

export function renderSearch(data, query) {
    const results = data.results || [];

    const typeIcons = {
        news: 'newspaper', page: 'file-lines', lab: 'flask', product: 'box-open',
    };

    const cards = results.map((item) => `
    <a href="${esc(localePath(item.url || '/'))}" class="nv-card">
      <div class="nv-card__body">
        <div class="nv-card__meta">
          ${icon(typeIcons[item.type] || 'file')}
          <span>${esc(item.type_label || item.type || '')}</span>
        </div>
        <h3 class="nv-card__title">${esc(item.title)}</h3>
        ${item.description
        ? `<p class="nv-card__text">${esc(truncate(item.description, 24))}</p>` : ''}
      </div>
    </a>`).join('');

    return `
    ${pageHero({
        title: gettext('Search results'),
        subtitle: query
            ? gettext('for “{query}”').replace('{query}', query)
            : '',
        breadcrumbs: [{label: gettext('Search')}],
        compact: true,
    })}
    <section class="nv-section">
      <div class="nv-container">
        <p class="nv-muted" style="margin-block-end: var(--nv-space-6);">
          <span class="nv-num">${num(results.length)}</span> ${gettext('results found')}
        </p>
        ${cards
        ? `<div class="nv-grid nv-grid--3">${cards}</div>`
        : emptyState({
            iconName: 'magnifying-glass',
            title: gettext('Nothing found'),
            text: gettext('Try a different search term.'),
        })}
      </div>
    </section>`;
}

/* ------------------------------------------------------------------ *
 * Resume page
 * ------------------------------------------------------------------ */

/**
 * A researcher's public profile. /api/resume/<username>/ has existed for a
 * while but nothing linked to it, so lab members' names were dead ends.
 */
export function renderResume(data) {
    const person = data || {};
    const name = person.display_name
        || `${person.first_name || ''} ${person.last_name || ''}`.trim()
        || person.username;

    const social = (person.social_links || []).map((link) => `
    <a href="${esc(link.url)}" class="nv-resume__social" target="_blank" rel="noopener"
       title="${esc(link.label)}">
      <i class="${esc(link.icon)}" aria-hidden="true"></i>
      <span class="nv-sr-only">${esc(link.label)}</span>
    </a>`).join('');

    const items = (person.items || []).map((item) => `
    <article class="nv-resume__item">
      <div class="nv-resume__item-body">
        ${item.link_label ? `<span class="nv-resume__item-kind">${esc(item.link_label)}</span>` : ''}
        <h3 class="nv-resume__item-title">${esc(item.text)}</h3>
        ${item.description ? `<p class="nv-resume__item-text">${esc(item.description)}</p>` : ''}
        ${(item.links || []).filter(Boolean).map((href) => `
          <a href="${esc(href)}" class="nv-resume__item-link" target="_blank" rel="noopener">
            ${icon('arrow-up-right-from-square')}<span>${esc(href)}</span>
          </a>`).join('')}
      </div>
    </article>`).join('');

    return `
    ${pageHero({
        title: name,
        subtitle: person.short_description || '', 
        breadcrumbs: [{label: name}],
        compact: true,
    })}
    <div class="nv-container nv-section">
            <div class="nv-split nv-split--sidebar-start" style="align-items: center; margin-block-end: var(--nv-space-8);">
        <div class="nv-split__aside" style="display: flex; flex-direction: column; align-items: center; text-align: center;">
          <div style="width: 200px; height: 200px; margin-bottom: var(--nv-space-4); border-radius: 50%; overflow: hidden; background: var(--nv-ink-100);">
            ${img(person.image, name, 'nv-resume__avatar')}
          </div>
          ${social ? `<div class="nv-resume__socials" style="justify-content: center; margin-bottom: 0;">${social}</div>` : ''}
        </div>
        <div>
          ${person.resume ? `<section class="nv-resume__bio nv-prose" style="max-inline-size: 100%; margin-bottom: 0;">${person.resume}</section>` : ''}
        </div>
      </div>
      ${items ? `
        <section class="nv-section nv-section--tight">
          <h2 class="nv-section-title">${gettext('Activities')}</h2>
          <div class="nv-resume__items">${items}</div>
        </section>` : ''}
         ${!person.resume && !items && !person.image ? emptyState({
        iconName: 'user',
        title: gettext('Nothing to show yet'),
        text: gettext('This profile has not been filled in.'),
    }) : ''}
    </div>`;
}
